import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Pencil, Type, Paperclip, Trash2, Search, FileText, Loader2, Users, Check, X, Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { useAuth } from "@/lib/auth-context";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/cn";
import {
  listNotes, createNote, patchNote, deleteNote,
  listNoteShares, addNoteShare, removeNoteShare,
  listOrgMembers, dashboardKeys, type Note, type NoteShare,
} from "@/lib/dashboard-api";

function bucketLabel(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const dayMs = 86400_000;
  const ageDays = Math.floor((now.getTime() - d.getTime()) / dayMs);
  if (ageDays === 0) return "TODAY";
  if (ageDays === 1) return "YESTERDAY";
  if (ageDays < 7) return "PREVIOUS 7 DAYS";
  if (ageDays < 30) return "PREVIOUS 30 DAYS";
  return "OLDER";
}

function previewBody(body: string): string {
  return body.split("\n").find((l) => l.trim().length > 0)?.slice(0, 60) ?? "—";
}

export function DashboardNotesCard() {
  const { sessionId, user } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();

  const { data: notes = [] } = useQuery<Note[]>({
    queryKey: [...dashboardKeys.notes(), sessionId],
    queryFn: () => listNotes(sessionId),
    enabled: !!sessionId,
  });

  const [activeId, setActiveId] = useState<string | null>(null);
  useEffect(() => {
    if (!activeId && notes.length) setActiveId(notes[0].id);
  }, [notes, activeId]);
  const active = useMemo(() => notes.find((n) => n.id === activeId) ?? null, [notes, activeId]);

  const [search, setSearch] = useState("");
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return notes;
    return notes.filter(
      (n) => (n.title ?? "").toLowerCase().includes(q) || n.body.toLowerCase().includes(q),
    );
  }, [notes, search]);

  const grouped = useMemo(() => {
    const out: Record<string, Note[]> = {};
    for (const n of filtered) {
      const k = bucketLabel(n.updatedAt);
      out[k] = out[k] ?? [];
      out[k].push(n);
    }
    return out;
  }, [filtered]);

  const create = useMutation({
    mutationFn: () => createNote({ title: "Untitled note", body: "" }, sessionId),
    onSuccess: (note) => {
      qc.invalidateQueries({ queryKey: dashboardKeys.notes() });
      setActiveId(note.id);
    },
  });
  const save = useMutation({
    mutationFn: (input: { id: string; title?: string | null; body?: string }) =>
      patchNote(input.id, { title: input.title, body: input.body }, sessionId),
    onSuccess: () => qc.invalidateQueries({ queryKey: dashboardKeys.notes() }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteNote(id, sessionId),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: dashboardKeys.notes() });
      if (activeId === id) setActiveId(null);
      toast({ title: "Note deleted" });
    },
  });

  // Title + body inline edits — save on blur
  const [titleDraft, setTitleDraft] = useState("");
  const [bodyDraft, setBodyDraft] = useState("");
  useEffect(() => {
    setTitleDraft(active?.title ?? "");
    setBodyDraft(active?.body ?? "");
  }, [activeId, active?.title, active?.body]);

  const initials = user?.firstName?.[0]?.toUpperCase()
    + (user?.lastName?.[0]?.toUpperCase() ?? "")
    || "U";

  return (
    <div className="rounded-lg border bg-card flex flex-col h-[420px]">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <FileText className="h-4 w-4" />
          Notes
        </h2>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => active && save.mutate({ id: active.id, title: titleDraft, body: bodyDraft })}
            disabled={!active || save.isPending}
            aria-label="Save"
          >
            {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Pencil className="h-3.5 w-3.5" />}
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" disabled aria-label="Type">
            <Type className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" disabled aria-label="Attach">
            <Paperclip className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-destructive"
            onClick={() => active && active.isOwner && remove.mutate(active.id)}
            disabled={!active || !active.isOwner}
            aria-label="Delete"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
          {active?.isOwner && <CollaboratePopover note={active} />}
          <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Search">
            <Search className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div className="flex-1 min-h-0 grid grid-cols-[180px_1fr]">
        {/* Index */}
        <div className="border-r overflow-y-auto p-2 space-y-3">
          <div className="px-1">
            <Input
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-7 text-xs"
            />
          </div>
          {Object.entries(grouped).map(([label, list]) => (
            <div key={label}>
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground px-2 mb-1">
                {label}
              </p>
              <ul className="space-y-0.5">
                {list.map((n) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => setActiveId(n.id)}
                      className={cn(
                        "w-full text-left rounded-md px-2 py-1.5 hover:bg-muted/50",
                        activeId === n.id && "bg-muted",
                      )}
                    >
                      <p className="text-xs font-medium truncate">
                        {n.title?.trim() || previewBody(n.body) || "Untitled"}
                      </p>
                      <p className="text-[10px] text-muted-foreground truncate">
                        {previewBody(n.body)}
                      </p>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="text-xs text-muted-foreground text-center py-3">
              No notes
            </div>
          )}
          <Button
            variant="outline"
            size="sm"
            className="w-full h-7 text-xs"
            onClick={() => create.mutate()}
            disabled={create.isPending}
          >
            {create.isPending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Plus className="h-3 w-3 mr-1" />}
            New note
          </Button>
        </div>

        {/* Body */}
        <div className="flex flex-col min-h-0">
          {active ? (
            <>
              <div className="flex items-center justify-between border-b px-4 py-2 text-xs text-muted-foreground">
                <span>
                  {active.isOwner
                    ? <>Created by you</>
                    : <>Shared with you</>
                  } · <span>{new Date(active.updatedAt).toLocaleDateString()}</span>
                </span>
                <Badge variant="outline" className="text-[10px]">
                  <span className="inline-block w-4 h-4 rounded-full bg-blue-500 text-white text-[10px] font-semibold leading-4 text-center mr-1.5">
                    {initials}
                  </span>
                  Collaborate
                </Badge>
              </div>
              <Input
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={() => active && titleDraft !== (active.title ?? "") && save.mutate({ id: active.id, title: titleDraft })}
                className="border-0 px-4 py-3 text-lg font-semibold focus-visible:ring-0 rounded-none"
                placeholder="Untitled note"
                readOnly={!active.canEdit}
              />
              <Textarea
                value={bodyDraft}
                onChange={(e) => setBodyDraft(e.target.value)}
                onBlur={() => active && bodyDraft !== active.body && save.mutate({ id: active.id, body: bodyDraft })}
                placeholder="Start typing…"
                className="flex-1 border-0 px-4 pt-0 pb-3 text-sm focus-visible:ring-0 resize-none rounded-none"
                readOnly={!active.canEdit}
              />
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
              Pick a note or create one
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Collaborate popover — owner-only
// ---------------------------------------------------------------------------
function CollaboratePopover({ note }: { note: Note }) {
  const { sessionId } = useAuth();
  const qc = useQueryClient();
  const sharesQ = useQuery<NoteShare[]>({
    queryKey: [...dashboardKeys.noteShares(note.id), sessionId],
    queryFn: () => listNoteShares(note.id, sessionId),
    enabled: !!sessionId,
  });
  const membersQ = useQuery({
    queryKey: [...dashboardKeys.orgMembers(), sessionId],
    queryFn: () => listOrgMembers(sessionId),
    enabled: !!sessionId,
  });

  const add = useMutation({
    mutationFn: (userId: string) => addNoteShare(note.id, { userId, canEdit: true }, sessionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dashboardKeys.noteShares(note.id) });
      qc.invalidateQueries({ queryKey: dashboardKeys.notes() });
    },
  });
  const remove = useMutation({
    mutationFn: (userId: string) => removeNoteShare(note.id, userId, sessionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dashboardKeys.noteShares(note.id) });
      qc.invalidateQueries({ queryKey: dashboardKeys.notes() });
    },
  });

  const sharedUserIds = new Set((sharesQ.data ?? []).map((s) => s.userId));

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Manage sharing">
          <Users className="h-3.5 w-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0">
        <div className="px-3 py-2 border-b">
          <p className="text-xs font-semibold">Sharing</p>
          <p className="text-[11px] text-muted-foreground">
            People you add can read and edit this note.
          </p>
        </div>
        <div className="max-h-64 overflow-y-auto">
          {(membersQ.data ?? []).map((m) => {
            const isShared = sharedUserIds.has(m.id);
            return (
              <div key={m.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-muted/50">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium truncate">
                    {(m.firstName + " " + m.lastName).trim() || m.email}
                  </p>
                  <p className="text-[11px] text-muted-foreground truncate">{m.email}</p>
                </div>
                {isShared ? (
                  <Button
                    variant="ghost" size="icon" className="h-6 w-6"
                    onClick={() => remove.mutate(m.id)}
                    aria-label="Remove"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                ) : (
                  <Button
                    variant="ghost" size="icon" className="h-6 w-6"
                    onClick={() => add.mutate(m.id)}
                    aria-label="Add"
                  >
                    <Check className="h-3 w-3" />
                  </Button>
                )}
              </div>
            );
          })}
          {(membersQ.data ?? []).length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-4">
              No teammates yet
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
